import { makeStyles } from '@material-ui/styles'
import { logoutUrl } from 'app/constants'
import store, { RootState } from 'app/store'
import { cacheActions } from 'core/caching/cacheReducers'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import { MessageTypes } from 'core/components/notifications/model'
import { validateSession } from 'core/containers/AppController'
import Button from 'core/elements/button'
import Modal from 'core/elements/modal'
import Text from 'core/elements/Text'
import { useToast } from 'core/providers/ToastProvider'
import { preferencesActions } from 'core/session/preferencesReducers'
import { sessionActions, SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import useScopedPreferences from 'core/session/useScopedPreferences'
import Theme from 'core/themes/model'
import { SESSION_EXPIRY_WARNING_THRESHOLD } from 'core/watchdog/session-timeout'
import { prop } from 'ramda'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useReactRouter from 'use-react-router'

// Helper function to format time in minutes and seconds
const formatTime = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
}

/**
 * @description
 * Modal component that warns users when their session is about to expire.
 * Shows 2 minutes before expiry with options to extend session or sign out.
 * @returns {JSX.Element} - The SessionExpiryModal component.
 */
const SessionExpiryModal: React.FC = () => {
  const classes = useStyles()
  const { history } = useReactRouter()
  const dispatch = useDispatch()
  const showToast = useToast()
  const session = useSelector<RootState, SessionState>(prop(sessionStoreKey))
  const { showSessionExpiryWarning, username } = session
  const { getUserPrefs } = useScopedPreferences()

  const [isExtending, setIsExtending] = useState(false)
  const [countdown, setCountdown] = useState<number>(SESSION_EXPIRY_WARNING_THRESHOLD)

  // Ref to store interval
  const intervalRef = useRef<NodeJS.Timeout>()

  // Update countdown every second
  useEffect(() => {
    if (showSessionExpiryWarning) {
      intervalRef.current && clearInterval(intervalRef.current)
      intervalRef.current = setInterval(() => {
        setCountdown((prev) => {
          // Auto sign out when countdown reaches 0
          if (prev <= 1) {
            handleSignOut()
            return 0
          }
          return prev - 1
        })
      }, 1000)
    }

    return () => {
      intervalRef.current && clearInterval(intervalRef.current)
    }
  }, [showSessionExpiryWarning, intervalRef])

  // Modal Action handlers
  const dismissSessionExpiryWarning = useCallback(() => {
    dispatch(
      sessionActions.updateSession({
        showSessionExpiryWarning: false,
        sessionExpiryWarningDismissed: true,
      }),
    )
  }, [dispatch])

  const handleSignOut = useCallback(() => {
    dismissSessionExpiryWarning()
    history.push(logoutUrl) //This is an intentional logout so we are redirecting to the logout URL
  }, [dismissSessionExpiryWarning, history])

  // Extend session handler
  const handleExtendSession = useCallback(async () => {
    setIsExtending(true)
    try {
      await validateSession(getUserPrefs, session, history)

      showToast('Session extended successfully', MessageTypes.success, 5000, {
        vertical: 'top',
        horizontal: 'center',
      })
      dismissSessionExpiryWarning()
    } catch (error) {
      showToast('Error extending session. You will be logged out.', MessageTypes.error, 5000, {
        vertical: 'top',
        horizontal: 'center',
      })
      handleSignOut()
    } finally {
      setIsExtending(false)
    }
  }, [dismissSessionExpiryWarning, handleSignOut, getUserPrefs, session, history])

  // If session expiry warning is not shown, do not render anything
  if (!showSessionExpiryWarning) {
    return null
  }

  return (
    <Modal
      open
      panel="dialog"
      onClose={dismissSessionExpiryWarning}
      onBackdropClick={dismissSessionExpiryWarning}
      title={
        <div className={classes.title}>
          <FontAwesomeIcon className={classes.icon} size="lg" solid>
            exclamation-triangle
          </FontAwesomeIcon>
          <Text variant="h4">Session Expiring Soon</Text>
        </div>
      }
      maxWidth={500}
      showOverlay={true}
      footer={
        <div className={classes.footer}>
          <Button variant="secondary" onClick={dismissSessionExpiryWarning} disabled={isExtending}>
            Dismiss
          </Button>
          <Button variant="secondary" onClick={handleSignOut} disabled={isExtending}>
            Sign Out
          </Button>
          <Button variant="primary" onClick={handleExtendSession} loading={isExtending} autoFocus>
            Stay Signed In
          </Button>
        </div>
      }
    >
      <div className={classes.content}>
        <Text variant="body2" component="div">
          Your session will automatically end in <strong>{formatTime(countdown)}</strong> due to
          inactivity. You can choose to stay signed in or sign out now.
        </Text>
        <Text variant="body2" component="div">
          If you click Dismiss, this message will close but you will still be logged out once the
          timer runs out.
        </Text>
      </div>
    </Modal>
  )
}

export default SessionExpiryModal

const useStyles = makeStyles<Theme>((theme) => ({
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  icon: {
    color: theme.palette.warning.main,
  },
  footer: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  note: {
    fontSize: 12,
  },
}))
